/**
 * Details tab for editing role name and description
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";

interface RoleDetailsTabProps {
	role: {
		id: string;
		name: string;
		description: string | null;
		isSystem: boolean;
	};
	onUpdate: () => void;
}

/**
 * Details tab for editing role name and description
 * @param root0 - Component props
 * @param root0.role - The role to edit
 * @param root0.onUpdate - Callback when the role is updated
 */
export function RoleDetailsTab({
	role,
	onUpdate,
}: RoleDetailsTabProps) {
	const [name, setName] = useState(role.name);
	const [description, setDescription] = useState(role.description ?? "");

	const updateMutation = trpc.roles.update.useMutation({
		onSuccess: () => {
			onUpdate();
		},
	});

	/**
	 * Saves the role details changes
	 */
	const handleSave = () => {
		updateMutation.mutate({
			roleId: role.id,
			name: name.trim() || undefined,
			description: description.trim() || null,
		});
	};

	const hasChanges = name !== role.name || description !== (role.description ?? "");

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<Label htmlFor="edit-name">Role Name</Label>
				<Input
					id="edit-name"
					value={name}
					onChange={(e) => { setName(e.target.value); }}
					disabled={role.isSystem}
				/>
				{role.isSystem && (
					<p className="text-xs text-muted-foreground">
						System roles cannot be renamed.
					</p>
				)}
			</div>

			<div className="space-y-2">
				<Label htmlFor="edit-description">Description</Label>
				<Textarea
					id="edit-description"
					value={description}
					onChange={(e) => { setDescription(e.target.value); }}
					rows={3}
				/>
			</div>

			{updateMutation.isError && (
				<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
					{updateMutation.error.message}
				</div>
			)}

			<Button
				onClick={handleSave}
				disabled={!hasChanges || updateMutation.isPending}
			>
				{updateMutation.isPending ? "Saving..." : "Save Changes"}
			</Button>
		</div>
	);
}
